import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingApi } from '../../utils/bookingApi';
import '../../pages/booking/booking-shared.css';
import './MembershipPaymentWarning.css';

// The problems a family can act on (the server's FAMILY_ISSUES)
export const FAMILY_ISSUES = ['PAYMENT_FAILED', 'NOT_SET_UP', 'ARREARS'];
// Staff-only notes where nothing is owed yet (the server's NOTE_ISSUES), such as
// a first payment still to come that covers this month
export const NOTE_ISSUES = ['FIRST_PAYMENT_AHEAD'];

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

// What the family is told about one membership's payment problem.
// item: { gymnast: { firstName }, issue, amount, directDebitCancelled };
// onMyAccount: shown on My account itself, so it doesn't send them there
export function paymentIssueText({ gymnast, issue, amount, directDebitCancelled }, { onMyAccount = false } = {}) {
  const there = onMyAccount ? '' : ' in My account';
  const owed = amount > 0 ? ` (${money(amount)} owed)` : '';
  const name = gymnast?.firstName || 'Your child';
  if (issue === 'PAYMENT_FAILED') {
    return directDebitCancelled
      ? `${name}'s membership payment didn't go through because the Direct Debit was cancelled at the bank${owed}. Please pay by card${there}.`
      : `${name}'s membership payment didn't go through${owed}. Please pay it or update your card${there}.`;
  }
  if (issue === 'NOT_SET_UP') return `${name}'s membership is waiting for payment to be set up. Please finish setting it up${there}.`;
  if (issue === 'ARREARS') return `${name}'s membership has ${amount > 0 ? money(amount) : 'money'} unpaid from earlier months. Please get in touch with the club to pay it.`;
  return null;
}

// Home page warning: the signed-in family's memberships with a payment
// problem. Shows nothing when there is none, or the list can't be loaded.
export default function MembershipPaymentWarning() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let live = true;
    // A failure of any kind (even a call that throws straight away) shows nothing
    Promise.resolve().then(() => bookingApi.getMyPaymentIssues())
      .then(res => { if (live) setItems(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const shown = items.map(i => ({ ...i, text: paymentIssueText(i) })).filter(i => i.text);
  if (!shown.length) return null;
  const toMyAccount = shown.some(i => i.issue !== 'ARREARS');

  return (
    <div className="membership-payment-warning" role="alert">
      <strong className="membership-payment-warning__title">Membership payment needed</strong>
      {shown.map(i => <p key={i.membershipId} className="membership-payment-warning__text">{i.text}</p>)}
      {toMyAccount && <Link to="/booking/my-account" className="bk-btn bk-btn--primary membership-payment-warning__btn">Go to My account</Link>}
    </div>
  );
}
